import React, { useState } from "react";
import { Icon } from "../icons";
import { addDays, fmtDateTime, todayStr } from "../lib/format";
import { jobsByStatus, weekStats } from "../lib/selectors";
import { useMcOps } from "../lib/McOpsContext";
import { StatCard } from "../components/Common";
import { JobSearch } from "../components/JobSearch";
import { ReminderForm } from "../components/ReminderForm";
import { callsDueToday } from "../lib/callSchedule";

const RemindersPanel = ({ scope, title, list, defaultDate }) => {
  const { state, calPop, toggleCalPop, openJob } = useMcOps();
  const [formOpen, setFormOpen] = useState(false);
  const remStats = weekStats(state.reminders.map((r) => ({ moveDate: r.date })));
  const slug = `reminders-${scope}`;

  return (
    <div className="panel" style={{ position: "relative" }}>
      <div className="panel-head">
        <span>{title}</span>
        <button
          type="button"
          className="cal-btn"
          title="Add a new reminder"
          style={{ marginLeft: "auto" }}
          onClick={() => setFormOpen((v) => !v)}
        >
          <Icon name="bell" />
        </button>
        <button
          type="button"
          className="cal-btn"
          title="Weekly call volume"
          onClick={(e) => {
            e.stopPropagation();
            toggleCalPop(slug);
          }}
        >
          <Icon name="calendar" />
        </button>
      </div>
      <div className={`cal-pop${calPop === slug ? " open" : ""}`}>
        <div className="cal-pop-title">Calls to make</div>
        <div className="cal-pop-row">
          <span>Past 7 days</span>
          <strong>{remStats.lastWeek}</strong>
        </div>
        <div className="cal-pop-row">
          <span>Next 7 days</span>
          <strong>{remStats.nextWeek}</strong>
        </div>
      </div>
      {formOpen ? (
        <div className="quick-rem-form">
          <ReminderForm defaultDate={defaultDate} compact />
        </div>
      ) : null}
      <div className="panel-body">
        {list.length ? (
          list.map((r) => (
            <div
              className={`list-item${r.jobId ? " clickable" : ""}`}
              key={r.id}
              onClick={() => r.jobId && openJob(r.jobId)}
              title={r.jobId ? "Open the job" : undefined}
            >
              <div
                className="li-icon"
                style={{ background: "var(--warning-light)", color: "var(--warning)" }}
              >
                <Icon name="bell" />
              </div>
              <div className="li-body">
                {r.text}
                <div className="meta">{r.jobLabel}</div>
              </div>
            </div>
          ))
        ) : (
          <div className="panel-empty">
            Nothing due {scope === "today" ? "today" : "tomorrow"}.
          </div>
        )}
      </div>
    </div>
  );
};

export const LandingPage = () => {
  const { state, openProgressScreen, openJob } = useMcOps();
  const today = todayStr();
  const yesterday = addDays(today, -1);

  const pastDayEsc = state.escalations.filter(
    (e) => e.createdAt.slice(0, 10) >= yesterday,
  );
  const openActions = state.escalations
    .filter((e) => e.status === "open")
    .concat(
      state.notifExternal
        .filter((n) => n.unattended)
        .map((n) => ({
          note: n.text,
          jobLabel: "",
          type: "unattended",
          createdAt: n.date,
          status: "open",
        })),
    );
  const remToday = state.reminders.filter((r) => r.date === today);
  const remTomorrow = state.reminders.filter((r) => r.date === addDays(today, 1));
  const openEsc = state.escalations.filter((e) => e.status === "open").length;
  const inProgressCount = jobsByStatus(state, "in-progress").length;
  const callsDue = callsDueToday(state.jobs, today);

  return (
    <div className="page active">
      <div className="page-title-row">
        <div>
          <h2 className="page-title">Summary</h2>
        </div>
        <JobSearch />
        <button
          type="button"
          className="badge-proto as-btn"
          onClick={openProgressScreen}
        >
          <span>
            <Icon name="activity" />
          </span>
          Today's Progress
        </button>
      </div>

      <div className="landing-grid">
        <StatCard
          icon="clipboard"
          color="blue"
          num={state.jobs.length}
          label="Total active jobs"
        />
        <StatCard icon="alert" color="red" num={openEsc} label="Open escalations" />
        <StatCard
          icon="bell"
          color="amber"
          num={remToday.length}
          label="Reminders today"
        />
        <StatCard
          icon="activity"
          color="purple"
          num={inProgressCount}
          label="Move in Progress"
        />
      </div>

      <div className="landing-cols">
        {/* CC / MIP / APF / ETA / ADF due today (Asana 1219802379957808). */}
        <div className="panel">
          <div className="panel-head">Calls Due Today ({callsDue.length})</div>
          <div className="panel-body">
            {callsDue.length ? (
              callsDue.map(({ job, call }) => (
                <div
                  className="list-item clickable"
                  key={job.id + call.code}
                  role="button"
                  tabIndex={0}
                  onClick={() => openJob(job.id)}
                  onKeyDown={(e) => e.key === "Enter" && openJob(job.id)}
                >
                  <div
                    className="li-icon"
                    style={{
                      background: call.overdue ? "var(--danger-light)" : "var(--warning-light)",
                      color: call.overdue ? "var(--danger)" : "var(--warning)",
                    }}
                  >
                    <Icon name="phone" />
                  </div>
                  <div className="li-body">
                    <strong>{call.code}</strong> · {job.clientName}
                    <div className="meta">
                      {call.label} · {job.zohoId}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="panel-empty">No calls due today.</div>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">Open Items Past Day</div>
          <div className="panel-body">
            {pastDayEsc.length ? (
              pastDayEsc.map((e) => (
                <div className="list-item" key={e.id}>
                  <div
                    className="li-icon"
                    style={{
                      background:
                        e.type === "ops" ? "var(--primary-soft)" : "var(--purple-light)",
                      color: e.type === "ops" ? "var(--primary)" : "var(--purple)",
                    }}
                  >
                    <Icon name="alert" />
                  </div>
                  <div className="li-body">
                    <span className={`badge ${e.status}`}>
                      <span className="bdot" />
                      {e.status}
                    </span>{" "}
                    {e.note}
                    <div className="meta">
                      {e.jobLabel} · {fmtDateTime(e.createdAt)}
                    </div>
                    <div className="chat-note">
                      <Icon name="msg" />
                      posted to Ops/Sales group chat
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="panel-empty">No escalations in the past day.</div>
            )}
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">Missed &amp; Unanswered</div>
          <div className="panel-body">
            {openActions.length ? (
              openActions.map((a, i) => (
                <div className="list-item" key={i}>
                  <div
                    className="li-icon"
                    style={{ background: "var(--danger-light)", color: "var(--danger)" }}
                  >
                    <Icon name="alert" />
                  </div>
                  <div className="li-body">
                    {a.note}
                    <div className="meta">
                      {a.jobLabel || ""} {fmtDateTime(a.createdAt)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="panel-empty">No open action items.</div>
            )}
          </div>
        </div>

        <RemindersPanel
          scope="today"
          title="Reminders — Today"
          list={remToday}
          defaultDate={today}
        />
        <RemindersPanel
          scope="tomorrow"
          title="Reminders — Day Ahead"
          list={remTomorrow}
          defaultDate={addDays(today, 1)}
        />
      </div>
    </div>
  );
};
